import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';
import { prepareTR90Discount } from './tr90-cart-offer.js';

/** Add both bundle products in one request, preserving existing cart lines and codes. */
export async function addTR90Bundle({ tr90Id, caseId, routes, sections, sectionsUrl, errorMessage, fetchImpl = fetch }) {
  const readCart = async () => {
    const response = await fetchImpl(`${routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(errorMessage);
    return response.json();
  };
  const cart = await readCart();
  await prepareTR90Discount({ cart, code: 'TR90BOGO', url: routes.cart_update_url, errorMessage, fetchImpl });
  const response = await fetchImpl(routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ items: [{ id: tr90Id, quantity: 2 }, { id: caseId, quantity: 1 }], sections, sections_url: sectionsUrl }),
  });
  const added = await response.json();
  if (!response.ok || added.status) throw new Error(added.description || errorMessage);
  return { cart: await readCart(), sections: added.sections };
}

/** Landing-only controller. Cart rendering, focus and closing stay owned by Horizon. */
class TR90LandingBundleComponent extends Component {
  requiredRefs = ['cartStatus'];
  #pending = false;

  handleKeydown(event) {
    if (event.key === ' ') this.addBundle(event);
  }

  async addBundle(event) {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (this.#pending) return;
    this.#pending = true;
    const buttons = this.querySelectorAll('[data-tr90-bundle]');
    for (const button of buttons) {
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
    }
    this.refs.cartStatus.hidden = true;
    try {
      const drawer = document.getElementById('cart-drawer');
      if (!drawer) throw new Error(this.dataset.errorMessage);
      await Promise.all(['theme-drawer', 'cart-items-component', 'cart-drawer-component'].map(name => customElements.whenDefined(name)));
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(element => element.dataset.sectionId).filter(Boolean))];
      const result = await addTR90Bundle({
        tr90Id: Number(this.dataset.tr90Variant), caseId: Number(this.dataset.caseVariant),
        routes: Theme.routes, sections, sectionsUrl: window.location.pathname, errorMessage: this.dataset.errorMessage,
      });
      const update = CartLinesUpdateEvent.createPromise();
      update.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result.cart), detail: {
        items: result.cart.items, sections: result.sections, didError: false, source: 'tr90-landing-bundle', itemCount: 3,
      } });
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product',
        lines: [{ merchandiseId: this.dataset.tr90Variant, quantity: 2 }, { merchandiseId: this.dataset.caseVariant, quantity: 1 }],
        promise: update.promise,
      }));
      drawer.open();
    } catch (error) {
      this.refs.cartStatus.textContent = error.message || this.dataset.errorMessage;
      this.refs.cartStatus.hidden = false;
      this.refs.cartStatus.focus();
    } finally {
      this.#pending = false;
      for (const button of buttons) {
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('tr90-landing-bundle-component')) customElements.define('tr90-landing-bundle-component', TR90LandingBundleComponent);
